// src/components/ReminderSettings.tsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export function ReminderSettings({ defaultUseText }: { defaultUseText?: boolean }) {
  const [userId, setUserId] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [morningEnabled, setMorningEnabled] = useState(true);
  const [morningHour, setMorningHour] = useState('8');
  const [morningMinute, setMorningMinute] = useState('00');
  const [morningAmPm, setMorningAmPm] = useState('AM');
  const [morningDays, setMorningDays] = useState<string[]>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);

  const [afternoonEnabled, setAfternoonEnabled] = useState(true);
  const [afternoonHour, setAfternoonHour] = useState('7');
  const [afternoonMinute, setAfternoonMinute] = useState('00');
  const [afternoonAmPm, setAfternoonAmPm] = useState('PM');
  const [afternoonDays, setAfternoonDays] = useState<string[]>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);

  const [useText, setUseText] = useState(defaultUseText ?? true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [timeZone, setTimeZone] = useState('America/Los_Angeles');

  const timeZones = [
    'Pacific/Honolulu', 'America/Anchorage', 'America/Los_Angeles',
    'America/Denver', 'America/Chicago', 'America/New_York',
    'America/Phoenix', 'America/Toronto', 'Europe/London',
    'Europe/Berlin', 'Asia/Tokyo', 'Asia/Kolkata', 'Australia/Sydney'
  ];

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minutes = ['00', '15', '30', '45'];
  const ampmOptions = ['AM', 'PM'];
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    async function loadSettings() {
      console.log('[ReminderSettings] Loading settings...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      setUserId(session.user.id);

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (!profile) return;

      if (defaultUseText === undefined) {
        setUseText(profile.phone_reminders_enabled !== false);
      }

      setPhoneNumber(profile.phone_number ?? '');
      setTimeZone(profile.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone);

      if (profile.reminder_time_am) {
        const [h, m] = profile.reminder_time_am.split(':');
        const hour = parseInt(h, 10);
        const ampm = hour < 12 ? 'AM' : 'PM';
        const hour12 = hour % 12 === 0 ? '12' : String(hour % 12);
        setMorningHour(hour12);
        setMorningMinute(m);
        setMorningAmPm(ampm);
      }

      if (profile.reminder_time_pm) {
        const [h, m] = profile.reminder_time_pm.split(':');
        const hour = parseInt(h, 10);
        const ampm = hour < 12 ? 'AM' : 'PM';
        const hour12 = hour % 12 === 0 ? '12' : String(hour % 12);
        setAfternoonHour(hour12);
        setAfternoonMinute(m);
        setAfternoonAmPm(ampm);
      }

      if (Array.isArray(profile.reminder_days_am)) {
        setMorningDays(profile.reminder_days_am);
      }

      if (Array.isArray(profile.reminder_days_pm)) {
        setAfternoonDays(profile.reminder_days_pm);
      }

      console.log('[ReminderSettings] Settings loaded');
    }

    loadSettings();
  }, [defaultUseText]);

  const toggleDay = (day: string, isMorning: boolean) => {
    if (isMorning) {
      setMorningDays(prev =>
        prev.includes(day)
          ? prev.filter(d => d !== day)
          : [...prev, day]
      );
    } else {
      setAfternoonDays(prev =>
        prev.includes(day)
          ? prev.filter(d => d !== day)
          : [...prev, day]
      );
    }
  };

  const handleSave = async () => {
    console.log('[ReminderSettings] Saving reminder settings...');
    if (useText && phoneNumber.trim() === '') {
      setErrorMessage('Phone number is required when text reminders are enabled.');
      return;
    }

    if (!userId) {
      console.error('[ReminderSettings] Missing user ID');
      return;
    }

    try {
      const morningTime = morningEnabled
        ? `${parseInt(morningHour) + (morningAmPm === 'PM' && morningHour !== '12' ? 12 : 0)}:${morningMinute}`
        : null;

      const afternoonTime = afternoonEnabled
        ? `${parseInt(afternoonHour) + (afternoonAmPm === 'PM' && afternoonHour !== '12' ? 12 : 0)}:${afternoonMinute}`
        : null;

      const {
        data: { session },
        error: sessionError
      } = await supabase.auth.getSession();

      if (sessionError || !session || !session.access_token) {
        console.error('[ReminderSettings] Invalid session or missing access token', sessionError);
        setErrorMessage('Please log in again');
        return;
      }

      const token = session.access_token;
      const email = session.user.email;

      const { data: profile } = await supabase
        .from('profiles')
        .select('name')
        .eq('id', userId)
        .single();

      const { error } = await supabase
        .from('profiles')
        .update({
          phone_number: useText ? phoneNumber : null,
          phone_reminders_enabled: useText,
          reminder_time_am: morningTime,
          reminder_time_pm: afternoonTime,
          reminder_days_am: morningEnabled ? morningDays : [],
          reminder_days_pm: afternoonEnabled ? afternoonDays : [],
          time_zone: timeZone
        })
        .eq('id', userId);

      if (error) throw error;

      console.log('[ReminderSettings] Settings updated in Supabase. Sending to webhook...');

      const webhookRes = await fetch('https://cdnbjnapvlbmarhuflsv.supabase.co/functions/v1/trigger-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          eventType: 'reminder_settings_update',
          payload: {
            name: profile?.name ?? '',
            email: email ?? '',
            useText,
            morningTime,
            morningDays: useText && morningEnabled ? morningDays : [],
            afternoonTime,
            afternoonDays: useText && afternoonEnabled ? afternoonDays : [],
            timeZone
          }
        })
      });

      if (!webhookRes.ok) {
        const errorText = await webhookRes.text();
        console.error('[ReminderSettings] Webhook failed:', errorText);
        throw new Error('Webhook proxy failed');
      }

      console.log('[ReminderSettings] Webhook sent successfully');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('[ReminderSettings] Failed to save reminder settings', error);
      setErrorMessage('Failed to save reminder settings');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

 return (
  <div className="space-y-6 text-white">
    {saveSuccess && (
      <div className="bg-green-600 text-white text-sm font-semibold px-4 py-2 rounded">
        ✅ Reminders Scheduled
      </div>
    )}

    {errorMessage && (
      <div className="bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded">
        ⚠️ {errorMessage}
      </div>
    )}

    <div className="space-y-2">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={useText}
          onChange={(e) => {
            console.log('[ReminderSettings] useText changed:', e.target.checked);
            const checked = e.target.checked;
            setUseText(checked);
            if (!checked) {
              setMorningEnabled(false);
              setAfternoonEnabled(false);
              setMorningDays([]);
              setAfternoonDays([]);
            } else {
              setMorningEnabled(true);
              setAfternoonEnabled(true);
              setMorningDays(weekdays);
              setAfternoonDays(weekdays);
            }
          }}
        />
        <span>Send phone/text reminder</span>
      </label>
      {useText && (
        <input
          type="tel"
          placeholder="Enter phone number"
          className="w-full px-3 py-2 rounded bg-white text-black"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
        />
      )}
    </div>

    <div className="space-y-2">
      <label htmlFor="timezone" className="block text-sm font-medium">
        Time Zone
      </label>
      <select
        id="timezone"
        className="w-full px-3 py-2 rounded bg-white text-black"
        value={timeZone}
        onChange={(e) => setTimeZone(e.target.value)}
      >
        {timeZones.map((tz) => (
          <option key={tz} value={tz}>{tz}</option>
        ))}
      </select>
    </div>

    {useText && (
      <div className="space-y-6">

        {/* Morning Reminder Settings */}
        <div>
          <h4 className="font-semibold text-lg mb-2">Morning Reminder</h4>
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={morningEnabled}
              onChange={(e) => setMorningEnabled(e.target.checked)}
            />
            Enable Morning Reminder
          </label>
          {morningEnabled && (
            <div className="flex items-center gap-2">
              <select
                value={morningHour}
                onChange={(e) => setMorningHour(e.target.value)}
                className="bg-white text-black rounded px-2 py-1"
              >
                {hours.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
              :
              <select
                value={morningMinute}
                onChange={(e) => setMorningMinute(e.target.value)}
                className="bg-white text-black rounded px-2 py-1"
              >
                {minutes.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select
                value={morningAmPm}
                onChange={(e) => setMorningAmPm(e.target.value)}
                className="bg-white text-black rounded px-2 py-1"
              >
                {ampmOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Afternoon Reminder Settings */}
        <div>
          <h4 className="font-semibold text-lg mb-2">Afternoon Reminder</h4>
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={afternoonEnabled}
              onChange={(e) => setAfternoonEnabled(e.target.checked)}
            />
            Enable Afternoon Reminder
          </label>
          {afternoonEnabled && (
            <div className="flex items-center gap-2">
              <select
                value={afternoonHour}
                onChange={(e) => setAfternoonHour(e.target.value)}
                className="bg-white text-black rounded px-2 py-1"
              >
                {hours.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
              :
              <select
                value={afternoonMinute}
                onChange={(e) => setAfternoonMinute(e.target.value)}
                className="bg-white text-black rounded px-2 py-1"
              >
                {minutes.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select
                value={afternoonAmPm}
                onChange={(e) => setAfternoonAmPm(e.target.value)}
                className="bg-white text-black rounded px-2 py-1"
              >
                {ampmOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>
    )}

    <button
      onClick={handleSave}
      className="w-full bg-blue-600 text-white font-semibold px-4 py-2 rounded hover:bg-blue-700 transition-colors"
    >
      Save Reminder Settings
    </button>
  </div>
);
}